import { SourceType } from './Enums';
import { getSearchParamCaseInsensitive } from './sourceParams';

export const SOURCE_DATA = 'SourceData';

export interface SourceData {
    sourceType?: SourceType;
    source?: string;
    sessionId?: string;
    sourceMedium?: string;
    sourceCampaign?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
}

export const getSourceData = () => {
    var cachedSourceDataString = localStorage.getItem(SOURCE_DATA);
    return cachedSourceDataString
        ? (JSON.parse(cachedSourceDataString) as SourceData)
        : ({} as SourceData);
};

export const setSourceData = (data: SourceData): void => {
    localStorage.setItem(SOURCE_DATA, JSON.stringify(data));
};

export const clearSourceData = () => {
    localStorage.removeItem(SOURCE_DATA);
};

// Mirrors mobile-web parseAndStoreSourceData: case-insensitive URL param parsing.
export const parseAndStoreSourceData = (search: string): void => {
    const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;
    const searchParams = new URLSearchParams(normalizedSearch);

    const utmKnock = getSearchParamCaseInsensitive(searchParams, 'utm_knock_source_title');
    const referrer = getSearchParamCaseInsensitive(searchParams, 'referrer');
    const referral = getSearchParamCaseInsensitive(searchParams, 'referral');
    const source = getSearchParamCaseInsensitive(searchParams, 'source');
    const ssid = getSearchParamCaseInsensitive(searchParams, 'ssid');
    const utmSource = getSearchParamCaseInsensitive(searchParams, 'utm_source');
    const utmMedium = getSearchParamCaseInsensitive(searchParams, 'utm_medium');
    const utmCampaign = getSearchParamCaseInsensitive(searchParams, 'utm_campaign');

    const referrerLower = referrer != null ? referrer.toLowerCase() : '';

    let sourceType: SourceType = SourceType.None;
    let resolvedSource = '';
    let sessionId: string | undefined;
    let hasSourceUpdate = false;

    if (utmKnock) {
        resolvedSource = utmKnock;
        sourceType = SourceType.Knock;
        hasSourceUpdate = true;
    } else if (referrerLower === 'betterbot.ai') {
        resolvedSource = source || 'BetterBot';
        sourceType = SourceType.BetterBot;
        hasSourceUpdate = true;
    } else if (referrerLower === 'hyly') {
        resolvedSource = source || 'Hyly';
        sourceType = SourceType.Hyly;
        sessionId = ssid != null ? ssid : undefined;
        hasSourceUpdate = true;
    } else if (referrerLower === 'funnel') {
        resolvedSource = source || 'Funnel';
        sourceType = SourceType.Funnel;
        hasSourceUpdate = true;
    } else if (utmSource) {
        resolvedSource = utmSource;
        sourceType = SourceType.None;
        hasSourceUpdate = true;
    } else if (referral) {
        resolvedSource = referral;
        sourceType = SourceType.Snippet;
        hasSourceUpdate = true;
    }

    const first_name = getSearchParamCaseInsensitive(searchParams, 'first_name');
    const last_name = getSearchParamCaseInsensitive(searchParams, 'last_name');
    const phone = getSearchParamCaseInsensitive(searchParams, 'phone');
    const email = getSearchParamCaseInsensitive(searchParams, 'email');
    const hasProfileUpdate = !!(first_name || last_name || phone || email);

    if (!hasSourceUpdate && !utmMedium && !utmCampaign && !hasProfileUpdate) {
        return;
    }

    const existing = getSourceData();
    const next: SourceData = { ...existing };

    if (hasSourceUpdate) {
        next.source = resolvedSource;
        next.sourceType = sourceType;
        if (sessionId !== undefined) {
            next.sessionId = sessionId;
        }
    }

    if (utmMedium) {
        next.sourceMedium = utmMedium;
    }
    if (utmCampaign) {
        next.sourceCampaign = utmCampaign;
    }

    if (first_name) {
        next.first_name = first_name;
    }
    if (last_name) {
        next.last_name = last_name;
    }
    if (phone) {
        next.phone = phone;
    }
    if (email) {
        next.email = email;
    }

    setSourceData(next);
};

export const withSourceMediumAndCampaign = <
    T extends { sourceMedium?: string | null; sourceCampaign?: string | null }
>(
    payload: T
): T => {
    const sourceData = getSourceData();
    return {
        ...payload,
        sourceMedium:
            payload.sourceMedium != null
                ? payload.sourceMedium
                : sourceData.sourceMedium != null
                ? sourceData.sourceMedium
                : null,
        sourceCampaign:
            payload.sourceCampaign != null
                ? payload.sourceCampaign
                : sourceData.sourceCampaign != null
                ? sourceData.sourceCampaign
                : null
    };
};



// WEBPACK FOOTER //
// ./src/utilities/sourceData.ts